import React, { useCallback, useState } from 'react'

import { bool, string } from 'prop-types'

import { AUTH_MODAL_EVENT, MODE } from 'Pods/LazyLoaded/AiSignInModal/constants'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import { buildSubscriptionHref, STUDIO_RETURN_URL } from 'Pods/ThreeDStudio/Shared/UsageBodies/constants'

import { NOOP } from 'Utils/stableDefaults'

import HeroIntro from './Components/HeroIntro/HeroIntro'
import SubscriptionCard from './Components/SubscriptionCard/SubscriptionCard'
import WaitlistModal from './Components/WaitlistModal/WaitlistModal'

// The SubscriptionCard layouts (see SubscriptionCard). Every layout shows the subscription;
// the flags add the optional CTAs on top of it.
const LAYOUTS = {
  // Subscription + "Go to CGTrader AI" for users who can already generate.
  studioLink: { showStudioButton: true },
  // Subscription + free-trial block (with the struck-through price) + "Try Free Generations".
  freeTrial: { showTrialBlock: true, showOriginalPrice: true },
  // Subscription + free-trial block + "Join the Waitlist" / "I have access code" (opens the modal).
  joinWaitlist: { showTrialBlock: true, showWaitlistCta: true },
  // Subscription only.
  subscriptionOnly: {},
}

// Each Generator::SubscriptionState#tier maps to one layout. The 'subscribed' tier renders no
// header at all -- the page opens straight into the gallery and the bottom CTABanner carries
// "Start Creating with AI".
const TIER_LAYOUT = {
  in_free_beta: LAYOUTS.studioLink,
  has_credits: LAYOUTS.studioLink,
  has_reward_credits: LAYOUTS.studioLink,
  must_join_waitlist: LAYOUTS.joinWaitlist,
  eligible_for_trial: LAYOUTS.freeTrial,
  not_subscribed: LAYOUTS.subscriptionOnly,
  // TODO: waitlisted needs its waitlist-specific flow (buying a subscription skips the waitlist).
  waitlisted: LAYOUTS.subscriptionOnly,
}

// Per-user subscriber state is not available at SSR on this cacheable fast page, but the header must
// still occupy its final height from first paint -- otherwise it mounts after hydration and shoves the
// title + gallery down (a ~0.38 CLS). So SSR (and the pre-hydration render) draws the signed-out default
// layout, chosen deterministically from the freeTrialAvailable SSR prop so it matches what a signed-out
// visitor resolves to once pageConfig lands -- no swap, no shift. Once pageConfig arrives it only refines
// the layout per tier (logged-in deltas are small) and collapses the header for subscribers (an upward
// shift limited to logged-in subscribers, outside the anonymous CrUX path). See CLAUDE.md "SSR-stable
// rendering on fast pages". extractingCoreStyles (true only during the critical-CSS extraction pass)
// force-renders every variant so their styles are captured.
function UpgradeHeader({ extractingCoreStyles = false, subscriptionPaymentFormUrl = '', freeTrialAvailable = false }) {
  const pageConfig = usePageConfig({ listener: 'full-page-config-loaded', checkIfAvailable: false })
  const [isWaitlistOpen, setIsWaitlistOpen] = useState(false)
  const [hasJoined, setHasJoined] = useState(false)

  const subscriptionHref = buildSubscriptionHref(
    pageConfig?.threeDStudioSubscriptionPaymentFormUrl || subscriptionPaymentFormUrl
  )

  // Server-sourced from the Subscription model (see has_fast_application_state_helpers.rb); undefined
  // pre-hydration / during critical-CSS extraction, when SubscriptionCard falls back to its literals.
  const price = pageConfig?.threeDStudioSubscriptionPrice
  const originalPrice = pageConfig?.threeDStudioSubscriptionOriginalPrice
  const monthlyModels = pageConfig?.threeDStudioSubscriptionMonthlyModels

  const handleOpenWaitlist = useCallback(() => {
    setIsWaitlistOpen(true)
  }, [])

  const handleCloseWaitlist = useCallback(() => {
    setIsWaitlistOpen(false)
  }, [])

  const handleJoined = useCallback(() => {
    setHasJoined(true)
  }, [])

  // Signed-out "Try Free Generations" cannot grant the trial directly; hand off to the in-page
  // AI sign-in modal (signup) via the global event bus, mirroring the header's Log in / Sign up
  // buttons. useAuthModalTrigger (at the AiSubLanding root) opens the modal; post-auth lands on /ai.
  const handleTryFreeGenerations = useCallback(() => {
    if (typeof App === 'undefined' || !App.events) return
    App.events.publish(AUTH_MODAL_EVENT, { mode: MODE.SIGNUP })
  }, [])

  let cards
  let waitlistModal = null

  if (extractingCoreStyles) {
    // Render every layout so the critical-CSS extraction pass captures all of their styles.
    cards = Object.entries(LAYOUTS).map(([name, layout]) => (
      <SubscriptionCard
        key={name}
        {...layout}
        subscriptionHref={subscriptionHref}
        studioHref={STUDIO_RETURN_URL}
        price={price}
        originalPrice={originalPrice}
        monthlyModels={monthlyModels}
        onJoinWaitlist={NOOP}
      />
    ))
  } else if (pageConfig?.threeDStudioAccessTier === 'subscribed') {
    // Subscribers: no header. This is the only tier whose header collapses post-hydration; the shift
    // is upward and limited to logged-in subscribers, outside the anonymous CrUX path the SSR default
    // protects. We cannot know this tier at SSR, so a brief signed-out flash here is the accepted cost.
    return null
  } else {
    // Pre-hydration there is no pageConfig; render the signed-out default so the SSR markup already
    // holds the band's final height (see the component comment). Once pageConfig lands, tier refines the
    // layout. Signed-out visitors ('none') get no tier: show the free-trial block when the (class-level)
    // trial pool has room, else subscription only; the trial CTA routes through sign-in since the trial
    // can only be granted once an account exists.
    const tier = pageConfig?.threeDStudioAccessTier
    const isSignedOut = !tier
    const trialAvailable = pageConfig ? pageConfig.threeDStudioFreeTrialAvailable : freeTrialAvailable
    const signedOutLayout = trialAvailable
      ? LAYOUTS.freeTrial
      : LAYOUTS.subscriptionOnly
    const baseLayout = TIER_LAYOUT[tier] || signedOutLayout

    // Once the user joins the waitlist they are server-side `waitlisted`; mirror that tier's layout
    // (subscription only) so the hero card matches a fresh reload instead of still inviting a join.
    const layout = hasJoined ? LAYOUTS.subscriptionOnly : baseLayout

    cards = (
      <SubscriptionCard
        {...layout}
        subscriptionHref={subscriptionHref}
        studioHref={STUDIO_RETURN_URL}
        price={price}
        originalPrice={originalPrice}
        monthlyModels={monthlyModels}
        onJoinWaitlist={handleOpenWaitlist}
        onTryFreeGenerations={isSignedOut ? handleTryFreeGenerations : undefined}
      />
    )

    // must_join_waitlist users open the free-beta waitlist modal from the card CTAs. Mount it off
    // the base tier, not the post-join layout, so joining (which switches the card above) does not
    // unmount the modal mid-flow and drop its confirmation view.
    if (baseLayout.showWaitlistCta) {
      waitlistModal = (
        <WaitlistModal
          isOpen={isWaitlistOpen}
          isJoined={hasJoined}
          onClose={handleCloseWaitlist}
          onJoined={handleJoined}
          subscriptionHref={subscriptionHref}
        />
      )
    }
  }

  return (
    <section className="ai-sub-landing__upgrade-header">
      <div className="ai-sub-landing__upgrade-header-inner">
        <HeroIntro />
        {cards}
      </div>
      {waitlistModal}
    </section>
  )
}

UpgradeHeader.propTypes = {
  extractingCoreStyles: bool,
  subscriptionPaymentFormUrl: string,
  freeTrialAvailable: bool,
}

export default UpgradeHeader
